/**
 * TypeScript types for the crafting system.
 */

import { ItemRecipe, ItemImage, Tag, CraftingMaterial } from './items';

/**
 * User resources for crafting affordability checks.
 */
export interface UserResources {
    tea: number;
    wood: number;
    mystical: number;
    medicinal: number;
    edible: number;
}

/**
 * Crafting cost as a resource map.
 */
export type CraftingCost = Partial<UserResources>;

/**
 * Craftable recipe with additional crafting info.
 */
export interface CraftableRecipe extends ItemRecipe {
    crafting_time_minutes: number;
    crafting_time_display: string;
    crafting_cost: CraftingCost;
    source_type: 'blueprint' | 'building';
    source_name: string;
    can_afford: boolean;
}

/**
 * Active crafting job.
 */
export interface CraftingJob {
    id: number;
    recipe: {
        id: number;
        name: string;
        image: ItemImage;
        rarity: {
            value: string;
            label: string;
            tailwindClass: string;
        };
    };
    source_type: 'blueprint' | 'building';
    resources_spent: CraftingCost;
    started_at: string;
    completes_at: string;
    time_remaining_seconds: number | null;
    time_remaining_display: string;
    progress_percent: number;
    is_complete: boolean;
}

/**
 * Minimal hex info for crafting page.
 */
export interface CraftingHex {
    id: number;
    name: string;
    q: number;
    r: number;
    terrain: string;
}

/**
 * Props for the Crafting page.
 */
export interface CraftingPageProps {
    hex: CraftingHex;
    recipes: CraftableRecipe[];
    currentJob: CraftingJob | null;
    userResources: UserResources;
    isOwner: boolean;
    /** Whether the user can collect resources from the hex */
    canCollect: boolean;
    /** ISO8601 timestamp of when the next collection is available */
    nextCollectionAt: string;
}

/**
 * Resource labels for display.
 */
export const RESOURCE_LABELS: Record<keyof UserResources, string> = {
    tea: 'Tea',
    wood: 'Wood',
    mystical: 'Mystical',
    medicinal: 'Medicinal',
    edible: 'Edible',
};

/**
 * Resource colors for display.
 */
export const RESOURCE_COLORS: Record<keyof UserResources, string> = {
    tea: 'text-green-400',
    wood: 'text-amber-400',
    mystical: 'text-purple-400',
    medicinal: 'text-red-400',
    edible: 'text-yellow-400',
};

/**
 * Check if user can afford a crafting cost.
 */
export function canAffordCost(resources: UserResources, cost: CraftingCost): boolean {
    for (const [type, amount] of Object.entries(cost)) {
        const available = resources[type as keyof UserResources] ?? 0;
        if (available < (amount ?? 0)) {
            return false;
        }
    }
    return true;
}

/**
 * Format a duration in seconds to a human-readable string.
 */
export function formatDuration(seconds: number): string {
    if (seconds <= 0) {
        return 'Complete';
    }

    if (seconds < 60) {
        return `${seconds}s`;
    }

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
        const remainingSeconds = seconds % 60;
        return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (hours < 24) {
        return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
    }

    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
}
